import type { MessageStatus } from '../types';

export type MessageFilterStatus = MessageStatus | 'all' | 'read' | 'unread';
export type MessageFilterDateRange = 'all' | 'today' | 'week' | 'month' | 'year';

export interface MessageFolderFilterCriteria {
  searchQuery: string;
  filterStatus: MessageFilterStatus;
  filterDateRange: MessageFilterDateRange;
  /** Local-part username of a profile alias; empty = all aliases */
  filterAlias: string;
}

const STORAGE_PREFIX = 'mailbox:messageFilters:';

export const DEFAULT_MESSAGE_FILTER_CRITERIA: MessageFolderFilterCriteria = {
  searchQuery: '',
  filterStatus: 'all',
  filterDateRange: 'all',
  filterAlias: '',
};

function storageKey(username: string): string {
  return `${STORAGE_PREFIX}${username.trim().toLowerCase()}`;
}

export function loadMessageFilterCriteria(username: string): MessageFolderFilterCriteria {
  if (!username.trim()) return { ...DEFAULT_MESSAGE_FILTER_CRITERIA };
  try {
    const raw = sessionStorage.getItem(storageKey(username));
    if (!raw) return { ...DEFAULT_MESSAGE_FILTER_CRITERIA };
    const parsed = JSON.parse(raw) as Partial<MessageFolderFilterCriteria>;
    return {
      searchQuery: typeof parsed.searchQuery === 'string' ? parsed.searchQuery : '',
      filterStatus: parsed.filterStatus ?? 'all',
      filterDateRange: parsed.filterDateRange ?? 'all',
      filterAlias: typeof parsed.filterAlias === 'string' ? parsed.filterAlias : '',
    };
  } catch {
    return { ...DEFAULT_MESSAGE_FILTER_CRITERIA };
  }
}

export function saveMessageFilterCriteria(username: string, criteria: MessageFolderFilterCriteria): void {
  if (!username.trim()) return;
  try {
    sessionStorage.setItem(storageKey(username), JSON.stringify(criteria));
  } catch {
    // ignore quota / private mode
  }
}

export function clearMessageFilterCriteria(username: string): MessageFolderFilterCriteria {
  if (username.trim()) {
    try {
      sessionStorage.removeItem(storageKey(username));
    } catch {
      // ignore
    }
  }
  return { ...DEFAULT_MESSAGE_FILTER_CRITERIA };
}

export function hasActiveMessageFilters(criteria: MessageFolderFilterCriteria): boolean {
  return (
    criteria.searchQuery.trim().length > 0 ||
    criteria.filterStatus !== 'all' ||
    criteria.filterDateRange !== 'all' ||
    criteria.filterAlias.trim().length > 0
  );
}
